import { useTranslation } from "react-i18next";
import { Check, MapPin, Sprout } from "lucide-react";
import { cn } from "@/lib/utils";
import { cultivoCuradoPorCodigo, type IdiomaCultivo } from "@/lib/crops/curados";
import type { ScanLocation } from "@/hooks/useScanCropChoices";

/**
 * «¿DÓNDE Y QUÉ CULTIVO?» — los chips del escáner (plan cultivos SIEX F7, 05-10-2026).
 *
 * La foto del escáner no viene de un sector: con dos toques la persona dice la ubicación
 * y el cultivo, y la IA compara con las plagas de ESE cultivo. «No lo sé» es una respuesta
 * válida (la lista sale de todos los cultivos del sitio). Con una sola ubicación no se
 * pregunta dónde. Sin cultivos abiertos en la ubicación, solo «No lo sé».
 * Solo con el interruptor `pest_crop_ai`; apagado, el escáner es el de antes.
 */
export interface EleccionCultivo {
  locationId: string | null;
  /** null = «No lo sé». */
  cropCode: number | null;
}

/** El nombre del cultivo en el idioma de la pantalla (curado) o el guardado. */
export function nombreCultivo(code: number, guardado: string, lang: string): string {
  const idioma = (lang || "es").split("-")[0] as IdiomaCultivo;
  const curado = cultivoCuradoPorCodigo(code);
  return curado?.etiquetas?.[idioma] ?? (idioma === "es" ? curado?.visible : undefined) ?? guardado;
}

/**
 * La elección por defecto: la última usada si sigue valiendo; si no, la única ubicación,
 * y el único cultivo de esa ubicación (con varios, «No lo sé»).
 */
export function eleccionPorDefecto(locations: ScanLocation[], ultima: EleccionCultivo | null): EleccionCultivo {
  const loc = (ultima?.locationId && locations.find((l) => l.id === ultima.locationId)) || (locations.length === 1 ? locations[0] : null);
  if (!loc) return { locationId: null, cropCode: null };
  if (ultima?.locationId === loc.id && ultima.cropCode != null && loc.crops.some((c) => c.code === ultima.cropCode)) {
    return { locationId: loc.id, cropCode: ultima.cropCode };
  }
  return { locationId: loc.id, cropCode: loc.crops.length === 1 ? loc.crops[0].code : null };
}

function Chip({
  activo,
  onClick,
  children,
  testId,
  disabled,
}: {
  activo: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testId: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={activo}
      data-testid={testId}
      data-active={activo ? "true" : "false"}
      className={cn(
        "inline-flex min-h-[38px] max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-transform active:scale-95 disabled:opacity-50",
        activo ? "border-[#16A971] bg-[#e8f7f0] text-[#0b6b46]" : "border-[#d7e3ee] bg-white text-[#2E4A63] hover:bg-[#f6fafc]",
      )}
    >
      {activo && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />}
      <span className="min-w-0 truncate">{children}</span>
    </button>
  );
}

export function LocationCropChips({
  locations,
  value,
  onChange,
  disabled = false,
  showTitle = true,
  className,
}: {
  locations: ScanLocation[];
  value: EleccionCultivo;
  onChange: (v: EleccionCultivo) => void;
  disabled?: boolean;
  /** Dentro de una hoja que ya lo pregunta en su título, sin repetirlo. */
  showTitle?: boolean;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  if (locations.length === 0) return null;
  const loc = locations.find((l) => l.id === value.locationId) ?? null;
  const elegirUbicacion = (id: string) => {
    const nueva = locations.find((l) => l.id === id);
    onChange({ locationId: id, cropCode: nueva && nueva.crops.length === 1 ? nueva.crops[0].code : null });
  };
  return (
    <div className={cn("space-y-3 rounded-2xl border border-[#e4edf5] bg-[#fbfdff] p-3", className)} data-testid="scan-crop-chips">
      {showTitle && <p className="text-sm font-bold text-foreground">{t("advanced.cropChips.title", "¿Dónde y qué cultivo?")}</p>}
      {locations.length > 1 && (
        <div className="space-y-1.5">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" aria-hidden />
            {t("advanced.cropChips.location", "Ubicación")}
          </p>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={t("advanced.cropChips.location", "Ubicación")}>
            {locations.map((l) => (
              <Chip key={l.id} activo={value.locationId === l.id} onClick={() => elegirUbicacion(l.id)} testId="scan-location-chip" disabled={disabled}>
                {l.name}
              </Chip>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-1.5">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
          <Sprout className="h-3.5 w-3.5" aria-hidden />
          {t("advanced.cropChips.crop", "Cultivo")}
        </p>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={t("advanced.cropChips.crop", "Cultivo")}>
          {(loc?.crops ?? []).map((c) => (
            <Chip
              key={c.code}
              activo={value.cropCode === c.code}
              onClick={() => onChange({ locationId: loc!.id, cropCode: c.code })}
              testId="scan-crop-chip"
              disabled={disabled}
            >
              {nombreCultivo(c.code, c.name, i18n.language)}
            </Chip>
          ))}
          <Chip
            activo={value.cropCode == null}
            onClick={() => onChange({ locationId: value.locationId, cropCode: null })}
            testId="scan-crop-unknown"
            disabled={disabled}
          >
            {t("advanced.cropChips.unknown", "No lo sé")}
          </Chip>
        </div>
        {loc && loc.crops.length === 0 && (
          <p className="text-[11px] text-muted-foreground">{t("advanced.cropChips.noCrops", "Esta ubicación no tiene cultivos abiertos.")}</p>
        )}
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        {t("advanced.cropChips.hint", "Con el cultivo, la IA compara la foto con las plagas de ese cultivo.")}
      </p>
    </div>
  );
}

export default LocationCropChips;
